import React, { useEffect, useRef, useState } from 'react';
import './ScrollFloat.css';

const ScrollFloat = ({ children, speed = 0.5, direction = 'up', className = '' }) => {
  const elementRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const updateOffset = () => {
      if (!elementRef.current) return;

      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const rect = elementRef.current.getBoundingClientRect();
      const elementTop = rect.top + scrollY;
      
      // Calculate position relative to viewport center
      const viewportCenter = scrollY + windowHeight / 2;
      const distanceFromCenter = elementTop - viewportCenter;
      
      // Normalize to -1 to 1 range
      const normalizedDistance = Math.max(-1, Math.min(1, distanceFromCenter / (windowHeight * 0.5)));
      
      // Calculate offset based on scroll position and direction
      let calculatedOffset = 0;
      const maxOffset = 50 * speed; // Increased for more visible effect
      
      switch (direction) {
        case 'up':
          calculatedOffset = -normalizedDistance * maxOffset;
          break;
        case 'down':
          calculatedOffset = normalizedDistance * maxOffset;
          break;
        case 'left':
          calculatedOffset = -normalizedDistance * maxOffset;
          break;
        case 'right':
          calculatedOffset = normalizedDistance * maxOffset;
          break;
        default:
          calculatedOffset = -normalizedDistance * maxOffset;
      }
      
      setOffset(calculatedOffset);
    };

    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateOffset);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    updateOffset(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [speed, direction]);

  const style = {
    transform: direction === 'left' || direction === 'right' 
      ? `translateX(${offset}px)` 
      : `translateY(${offset}px)`,
    willChange: 'transform',
    transition: 'none', // Ensure no CSS transitions interfere
  };

  return (
    <span ref={elementRef} className={`scroll-float ${className}`} style={style}>
      {children}
    </span>
  );
};

export default ScrollFloat;
